import * as React from 'react';
import { useSelector } from 'react-redux';

import {
  INewbuildingPremiumBuilderStats,
  INewbuildingPremiumBuilderLogoProps,
} from '../../../../../components/AuthorAsideNew';
import { selectOfferId } from '../../../../../selectors';
import { selectBuilderInfo, selectBuilderStats } from '../../../../../selectors/newbuildingPremiumRate';
import { getNewbuildingsListQueryStringUrl } from '../../../../../utils/formatters';
import { pluralize } from '../../../../../utils/pluralize';
import { trackBuilderLogoClick, trackBuilderStatClick } from '../tracking';

export interface IBuilderPropsResponse {
  builderProps: INewbuildingPremiumBuilderLogoProps | null;
  builderStats: INewbuildingPremiumBuilderStats[];
  builderLabel: boolean;
}

export const usePremiumBuilderProps = (): IBuilderPropsResponse => {
  const builder = useSelector(selectBuilderInfo);
  const stats = useSelector(selectBuilderStats);
  const offerId = useSelector(selectOfferId);

  const onBuilderLogoClick = React.useCallback(() => {
    trackBuilderLogoClick({ offerId, builderId: builder?.id });
  }, [builder, offerId]);

  const onBuilderFactClick = React.useCallback(
    label => {
      trackBuilderStatClick({ offerId, label });
    },
    [stats, offerId],
  );

  const builderProps = builder
    ? ({
        logoUrl: builder?.logoUrl,
        builderName: builder?.name,
        url: builder?.url,
        onClick: onBuilderLogoClick,
      } as INewbuildingPremiumBuilderLogoProps)
    : null;

  const builderLabel = !!builder?.isReliable;

  const builderStats: INewbuildingPremiumBuilderStats[] = [];

  const yearFoundation = builder?.yearFoundation;
  if (yearFoundation) {
    builderStats.push({
      testId: 'year-foundation-stat',
      title: 'Год основания',
      value: `${yearFoundation}`,
    });
  }

  if (stats && stats.newbuildingCount) {
    builderStats.push({
      testId: 'newbuilding-count-stat',
      title: 'Жилых комплексов',
      value: `${stats.newbuildingCount.value}`,
      link: getNewbuildingsListQueryStringUrl(stats.newbuildingCount.qs),
      onClick: () => onBuilderFactClick('floating_block_JK'),
    });
  }

  if (stats && stats.housesDone && stats.housesDone.housesCount && stats.housesDone.newbuildingCount) {
    builderStats.push({
      testId: 'houses-done-stat',
      title: 'Сдано',
      value: `${pluralize(stats.housesDone.housesCount, ['дом', 'дома', 'домов'], false)} в ${
        stats.housesDone.newbuildingCount
      } ЖК`,
      link: getNewbuildingsListQueryStringUrl(stats.housesDone.qs),
      onClick: () => onBuilderFactClick('floating_block_ready_JK'),
    });
  }

  if (
    stats &&
    stats.housesInProgress &&
    stats.housesInProgress.housesCount &&
    stats.housesInProgress.newbuildingCount
  ) {
    builderStats.push({
      testId: 'houses-in-progress-stat',
      title: 'Строится',
      value: `${pluralize(stats.housesInProgress.housesCount, ['дом', 'дома', 'домов'], false)} в ${
        stats.housesInProgress.newbuildingCount
      } ЖК`,
      link: getNewbuildingsListQueryStringUrl(stats.housesInProgress.qs),
      onClick: () => onBuilderFactClick('floating_block_construction_JK'),
    });
  }

  return React.useMemo<IBuilderPropsResponse>(() => {
    return {
      builderProps,
      builderLabel,
      builderStats,
    };
  }, [builderProps, builderLabel, builderStats]);
};
